import { el, clear, panel, empty, chip, button, row, table, tile, toast } from '../ui.js';
import { get, ApiError } from '../api.js';

/**
 * Can this agency actually work?
 *
 * ---------------------------------------------------------------------------
 * THE TWO WAYS A FLOOR STOPS, NEITHER OF WHICH ANNOUNCES ITSELF
 * ---------------------------------------------------------------------------
 *
 * A producer with no licence on file finds out by sitting in front of an empty
 * queue. The calling screen now names the reason, which is a real improvement
 * and still the wrong moment: it tells the one person who cannot fix it, on the
 * morning they were supposed to start producing, one agent at a time.
 *
 * Leads in a state where NOBODY is licensed make no noise at all. No agent sees
 * them, so no agent complains, so nothing reports it. They were paid for. The
 * agency keeps buying more. The only evidence is an absence, which is why this
 * is the only screen in the product that can show it.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS SCREEN WILL NOT DO
 * ---------------------------------------------------------------------------
 *
 * It does not say a licence is valid. Only a state says that, and a number
 * typed into a box is a claim. Everything here is "what is on file" — said in
 * those words, because a compliance screen that overstates what it knows is
 * worse than no screen. The word "verified" is kept out of this file entirely,
 * and a test enforces that, so nobody can reach for it later in a sentence that
 * looks harmless on its own.
 */

interface Person {
  userId: string;
  name: string;
  role: string;
  status: string;
  states: string[];
  expiring: Array<{ state: string; expiresAt: string; days: number }>;
  expired: string[];
  canDial: boolean;
  expected: boolean;
}

interface Report {
  asOf: string;
  horizonDays: number;
  people: Person[];
  book: Array<{ state: string; leads: number; licensed: string[] }>;
  uncovered: Array<{ state: string; leads: number }>;
  stateless: number;
  counts: {
    people: number;
    producers: number;
    producersBlocked: number;
    expiringSoon: number;
    expired: number;
    coveredLeads: number;
    uncoveredLeads: number;
    canDialAnything: boolean;
    hasBook: boolean;
  };
}

const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner', admin: 'Administrator', manager: 'Manager',
  agent: 'Agent', compliance: 'Compliance',
};

export function readinessView(go: (p: string) => void): HTMLElement {
  const host = el('div');
  const stage = el('div');

  host.append(el('div', { class: 'vhead' },
    el('span', { class: 'kicker', text: 'Reports' }),
    el('h1', { text: 'Ready to work' }),
    el('p', {
      text: 'Who holds a licence and where, whose is running out, and how many leads are '
        + 'sitting in states nobody here can legally call.',
    })));
  host.append(stage);

  const load = async (): Promise<void> => {
    clear(stage);
    stage.append(el('div', { class: 'note', text: 'Reading…' }));
    try {
      const r = await get<Report>('/v1/readiness');
      clear(stage);
      draw(stage, r, load, go);
    } catch (e) {
      clear(stage);
      const msg = e instanceof ApiError ? e.message : 'Could not read this.';
      stage.append(el('div', { class: 'warnbox' }, el('b', null, msg)));
      toast(msg, 'crit');
    }
  };
  void load();
  return host;
}

function draw(
  host: HTMLElement, r: Report, reload: () => Promise<void>, go: (p: string) => void,
): void {
  const c = r.counts;

  /* ------------------------------------------------------- the headline --- */
  /*
   * A ZERO IS NOT ALWAYS GOOD NEWS, AND THE SUBTITLES USED TO SAY IT WAS.
   *
   * "0 producers blocked — everybody can dial" is false when there are no
   * producers, and "0 leads nobody can call — every state is covered" is false
   * when there is no book. Both of those are the day-one state, which is the
   * state this screen will be opened in first. A green tile explaining an empty
   * room is the same lie as a green banner over one, just smaller.
   */
  const tiles = el('div', { class: 'tiles' });
  const blockedNote = c.producers === 0
    ? 'no producers on the roster yet'
    : c.producersBlocked === 0 ? 'every producer can dial' : 'cannot dial anything today';
  tiles.append(tile(
    'Producers blocked', String(c.producersBlocked), blockedNote,
    c.producersBlocked > 0 ? 'var(--crit)' : c.producers === 0 ? 'var(--ink-3)' : 'var(--good)',
  ));
  const uncoveredNote = !c.hasBook
    ? 'no leads in the book yet'
    : c.uncoveredLeads === 0 ? 'every state is covered' : 'in states with no licence on file';
  tiles.append(tile(
    'Leads nobody can call', String(c.uncoveredLeads), uncoveredNote,
    c.uncoveredLeads > 0 ? 'var(--crit)' : c.hasBook ? 'var(--good)' : 'var(--ink-3)',
  ));
  tiles.append(tile(
    'Running out', String(c.expiringSoon), `within ${r.horizonDays} days`,
    c.expiringSoon === 0 ? 'var(--aqua)' : 'var(--warn)',
  ));
  tiles.append(tile('Already expired', String(c.expired), 'on file, no longer live',
    c.expired === 0 ? 'var(--aqua)' : 'var(--crit)'));
  host.append(tiles);

  /*
   * The one line worth reading if nothing else is. Said plainly, with the fix
   * named, because whoever is reading this is whoever can do it.
   *
   * THE DAY-ONE CASE COMES FIRST, AND IT USED NOT TO EXIST.
   *
   * A brand-new agency is one owner, no staff, no leads, no licence typed in
   * yet. Every count is then zero — nobody blocked, nothing uncovered, nothing
   * expired — and the first version of this read those zeros and printed
   * "nothing is blocking the floor" to somebody who could not dial a single
   * number. Exactly the failure the calling screen had with "this will not stay
   * true for long": a green light derived from an empty room.
   *
   * So the green box requires that SOMEBODY can actually dial.
   */
  if (!c.canDialAnything) {
    host.append(el('div', { class: 'warnbox' },
      el('b', null, 'Nobody here can dial yet.'),
      el('span', {
        text: ' No live licence is on file for anybody at this agency, so every lead will be '
          + 'refused at the moment somebody presses Call. Add licences in Settings → People — '
          + 'one per person, per state.',
      })));
  } else if (c.producersBlocked === 0 && c.uncoveredLeads === 0 && c.expired === 0) {
    host.append(el('div', { class: 'okbox' },
      el('b', null, 'Nothing is blocking the floor.'),
      el('span', {
        text: c.hasBook
          ? ' Every producer holds a live licence, and every state in the book has somebody '
            + 'who can call it.'
          : ' Everybody here holds a live licence. There are no leads in the book yet, so '
            + 'there is nothing yet to check them against.',
      })));
  } else {
    const lines: string[] = [];
    if (c.producersBlocked > 0) {
      lines.push(`${c.producersBlocked === 1 ? 'One producer holds' : `${c.producersBlocked} producers hold`} `
        + 'no live licence and cannot dial anything.');
    }
    if (c.uncoveredLeads > 0) {
      lines.push(`${c.uncoveredLeads.toLocaleString()} lead${c.uncoveredLeads === 1 ? ' is' : 's are'} `
        + 'in states nobody here is licensed for. Nobody will ever see them in a queue.');
    }
    if (c.expired > 0) {
      lines.push(`${c.expired} licence${c.expired === 1 ? ' has' : 's have'} run out.`);
    }
    host.append(el('div', { class: 'warnbox' },
      el('b', null, 'The floor is not fully able to work.'),
      el('span', { text: ` ${lines.join(' ')}` })));
  }

  /* ------------------------------------------------------------ people --- */
  const rows = r.people.map((p) => {
    const states = el('div', { style: 'display:flex;gap:4px;flex-wrap:wrap' });
    if (p.states.length === 0) {
      states.append(chip('none on file', 'crit'));
    } else {
      for (const s of p.states) {
        const soon = p.expiring.find((e) => e.state === s);
        states.append(chip(soon ? `${s} · ${soon.days}d` : s, soon ? 'warn' : ''));
      }
    }
    for (const s of p.expired) states.append(chip(`${s} expired`, 'crit'));

    /*
     * The note says what to DO, not what is wrong. "No licence on file" is a
     * diagnosis; "add it in Settings → People" is the next action, and the only
     * people who can open this screen are the ones who can take it.
     */
    let note = '';
    let bad = false;
    if (p.status === 'invited') note = 'Has not accepted their invitation yet.';
    else if (p.status === 'suspended') note = 'Suspended — switched off deliberately.';
    else if (!p.canDial && p.expected) {
      note = 'Cannot dial. Add their licence in Settings → People.';
      bad = true;
    } else if (!p.canDial) {
      /*
       * An owner or administrator may never personally sell, so this is not an
       * alarm — but "only matters if they dial" was too soft for the case that
       * actually happens, which is a new agency where the owner IS the floor.
       * It states the fact and names the fix without nagging somebody who will
       * never pick up a telephone.
       */
      note = 'No licence on file, so they cannot dial. Add one if they will.';
    } else if (p.expiring.length > 0) {
      /*
       * An amber chip reading "MI · 39D" means nothing to somebody seeing this
       * screen for the first time. A licence that lapses stops the dialling
       * silently, so the row spells it out.
       */
      const soonest = p.expiring[0]!;
      note = p.expiring.length === 1
        ? `Their ${soonest.state} licence runs out in ${soonest.days} days.`
        : `${p.expiring.length} licences running out, the first (${soonest.state}) `
          + `in ${soonest.days} days.`;
    }

    /*
     * A ROW, NOT A TABLE ROW — BECAUSE OF THE PHONE.
     *
     * This was a three-column table. On a 400px screen it scrolled sideways and
     * the note — the only part saying what to do — sat off the right edge, so
     * the screen showed a problem and hid the fix. Folding the note into the
     * name column moved the licence chips off the edge instead, which is the
     * other half of the information.
     *
     * `row()` stacks on a narrow screen by design, so nothing has to be chosen
     * between. This screen is read by somebody checking on a floor, and that
     * happens on a telephone as often as at a desk.
     */
    const sub = el('div');
    sub.append(el('div', { class: 's', style: 'color:var(--ink-3);font-size:12px',
      text: ROLE_LABEL[p.role] ?? p.role }));
    if (note) {
      sub.append(el('div', {
        style: `font-size:12.5px;margin-top:2px;color:${bad ? 'var(--crit)' : 'var(--ink-3)'}`,
        text: note,
      }));
    }
    return row({ title: p.name || '—', sub, right: states, onclick: () => go('/settings/people') });
  });

  host.append(panel(`People — ${c.people}`,
    r.people.length === 0
      ? empty('Nobody here yet', 'Invite people in Settings → People.')
      : el('div', null, ...rows),
    el('p', { class: 's', style: 'color:var(--ink-3);font-size:12px;margin-top:8px',
      text: 'What is on file. A licence number typed into a box is a claim, and nothing here '
        + 'checks it against a state — this screen reports the claim and when it runs out.' })));

  /* ------------------------------------------------------------- book --- */
  if (r.uncovered.length > 0) {
    const un = table(['State', 'Leads nobody can call'], r.uncovered.map((u) => ({
      cells: [el('b', { text: u.state }), el('span', { text: u.leads.toLocaleString() })],
    })));
    host.append(panel('States with no licence on file', un,
      el('p', { class: 's', style: 'color:var(--ink-3);font-size:12.5px;margin-top:8px',
        text: 'These leads were paid for and no agent will ever be offered them. Either somebody '
          + 'gets licensed there, or stop buying that state — this is the only screen that can '
          + 'tell you, because nobody sees these leads and so nobody reports them.' })));
  }

  if (r.stateless > 0) {
    host.append(el('div', { class: 'warnbox' },
      el('b', null, `${r.stateless.toLocaleString()} lead${r.stateless === 1 ? '' : 's'} with no state recorded.`),
      el('span', {
        text: ' No licence can be checked against a lead whose state is unknown, so nobody can '
          + 'call them — and they look completely normal on every other screen.',
      })));
  }

  if (r.book.length > 0) {
    host.append(panel('The book, by state', table(
      ['State', 'Leads', 'Who can call it'],
      r.book.map((b) => ({
        cells: [
          el('b', { text: b.state }),
          el('span', { text: b.leads.toLocaleString() }),
          b.licensed.length === 0
            ? chip('nobody', 'crit')
            : el('span', {
              text: b.licensed
                .map((id) => r.people.find((p) => p.userId === id)?.name ?? 'somebody')
                .join(', '),
            }),
        ],
      })),
    )));
  }

  const foot = el('div', { style: 'display:flex;gap:8px;margin-top:14px;flex-wrap:wrap' });
  foot.append(button('Refresh', 'sm', () => void reload()));
  foot.append(button('Settings → People', 'sm pri', () => go('/settings/people')));
  host.append(foot);
}
